import { BybButton } from "@Components/common/byb-ds";
import BybVrDialog, { BybVrDialogContent, BybVrDialogFooter, BybVrDialogHeader } from "@Components/game/common/dialog/byb-vr-dialog";
import {
  CentralCircle,
  FootballField,
  GoalkeeperBox,
  HoverableSection,
  PenaltyBox,
  PitchBoundaries,
  PositionCircle,
  PositionLabel,
  VerticalLine,
} from "@Components/game/common/styled-components";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import { DEFAULT_POSITION_SELECTOR_CONFIG, flattenPositionConfig, PositionSelectorConfig } from "@Constants/game/position-config";
import { getPositionsFromSection, getSectionFromPosition } from "@Constants/game/position-sections";
import PlayPositions from "@Enums/PlayPositions";
import FieldPositions from "@Enums/SimplifiedPlayPositions";
import StorageKeys from "@Enums/StorageKeys";
import { usePositionSelection } from "@hooks/game/use-position-selection";
import useI18n from "@i18n";
import { Button, Stack, Typography, useTheme } from "@mui/material";

const EMPTY_POSITIONS: PlayPositions[] = [];

interface IPositionSelectorDialog {
  isOpen: boolean;
  onClose: () => void;
  onPositionsSelected: (positions: PlayPositions[]) => void;
  initialPositions?: PlayPositions[];
  showSectionChips?: boolean;
  selectorConfig?: PositionSelectorConfig;
  storageKey?: StorageKeys;
}

const PositionSelectorDialog = ({
  isOpen,
  onClose,
  onPositionsSelected,
  initialPositions = EMPTY_POSITIONS,
  showSectionChips = true,
  selectorConfig = DEFAULT_POSITION_SELECTOR_CONFIG,
  storageKey = StorageKeys.SELECTED_POSITIONS,
}: IPositionSelectorDialog) => {
  const { i18n } = useI18n();
  const theme = useTheme();

  const {
    selectedPositions,
    setSelectedPositions,
    selectRandomPositions,
    selectAllPositionsFromSection,
    hoveredSection,
    setHoveredSection,
    handleConfirm,
    handleCancel,
  } = usePositionSelection({ initialPositions, onPositionsSelected, onCancel: onClose, selectorConfig, storageKey });

  const positions = flattenPositionConfig(selectorConfig.positions);

  const isPositionSelected = (position: PlayPositions) => {
    return selectedPositions.includes(position);
  };

  const isSectionSelected = (section: FieldPositions) => {
    const sectionPositions = getPositionsFromSection(section, selectorConfig.sections);
    return sectionPositions.length > 0 && sectionPositions.every(pos => selectedPositions.includes(pos));
  };

  const handleDialogClose = () => {
    handleCancel();
  };

  return (
    <BybVrDialog name="position-selector" open={isOpen} onClose={handleDialogClose}>
      <BybVrDialogHeader>
        <Typography variant="text4xl700" color="dsBase.v200" width="100%">
          {i18n("position.selection.dialog.title")}
        </Typography>
      </BybVrDialogHeader>

      <BybVrDialogContent width={"60vw"}>
        <Stack alignItems="center" spacing={2} width={"100%"} boxSizing="border-box">
          {showSectionChips && (
            <Stack direction="row" justifyContent="space-between" width="90%" spacing={1.5}>
              {selectorConfig.sections.map(section => (
                <HapticsButtonWrapper key={section.id}>
                  <PositionLabel
                    variant="textLg500"
                    isActive={hoveredSection === section.id || isSectionSelected(section.id)}
                    onMouseEnter={() => setHoveredSection(section.id)}
                    onMouseLeave={() => setHoveredSection(null)}
                    onClick={() => {
                      setHoveredSection(null);
                      selectAllPositionsFromSection(section.id);
                    }}
                  >
                    {i18n(section.labelKey)}
                  </PositionLabel>
                </HapticsButtonWrapper>
              ))}
            </Stack>
          )}
          <FootballField>
            <PitchBoundaries>
              <PenaltyBox sx={{ borderRadius: theme.spacing(0, 0.5, 0.5, 0), justifyContent: "flex-start", right: 2 }}>
                <GoalkeeperBox sx={{ borderRadius: theme.spacing(0, 0.5, 0.5, 0), right: 2 }} />
              </PenaltyBox>
              <CentralCircle />
              <PenaltyBox sx={{ borderRadius: theme.spacing(0.5, 0, 0, 0.5), justifyContent: "flex-end", left: 2 }}>
                <GoalkeeperBox sx={{ borderRadius: theme.spacing(0.5, 0, 0, 0.5), left: 2 }} />
              </PenaltyBox>
            </PitchBoundaries>
            <VerticalLine leftPosition={25} />
            <VerticalLine leftPosition={50} />
            <VerticalLine leftPosition={75} />

            {/* Hoverable sections */}
            <HoverableSection
              left={"0%"}
              isHovered={hoveredSection === FieldPositions.GOALKEEPER}
              isSelected={showSectionChips && isSectionSelected(FieldPositions.GOALKEEPER)}
              sx={{ borderRadius: theme.spacing(2, 0, 0, 2) }}
            />
            <HoverableSection
              left={"25%"}
              isHovered={hoveredSection === FieldPositions.DEFENSIVE}
              isSelected={showSectionChips && isSectionSelected(FieldPositions.DEFENSIVE)}
            />
            <HoverableSection
              left={"50%"}
              isHovered={hoveredSection === FieldPositions.MIDFIELD}
              isSelected={showSectionChips && isSectionSelected(FieldPositions.MIDFIELD)}
            />
            <HoverableSection
              left={"75%"}
              isHovered={hoveredSection === FieldPositions.FORWARD}
              isSelected={showSectionChips && isSectionSelected(FieldPositions.FORWARD)}
              sx={{ borderRadius: theme.spacing(0, 2, 2, 0) }}
            />

            {positions.map((pos, index) => {
              const positionSection = getSectionFromPosition(pos.id, selectorConfig.sections);
              return (
                <HapticsButtonWrapper key={`${pos.id}-${index}`}>
                  <PositionCircle
                    isSelected={isPositionSelected(pos.id)}
                    isHovered={hoveredSection === positionSection}
                    onClick={() => {
                      if (!isPositionSelected(pos.id)) {
                        setSelectedPositions(showSectionChips ? [...selectedPositions, pos.id] : [pos.id]);
                        return;
                      }

                      setSelectedPositions(selectedPositions.filter((p: PlayPositions) => p !== pos.id));
                    }}
                    sx={{
                      top: pos.position.top,
                      left: pos.position.left,
                    }}
                  >
                    <Typography variant="textS600" color="dsBase.v200">
                      {i18n(pos.nameKey)}
                    </Typography>
                  </PositionCircle>
                </HapticsButtonWrapper>
              );
            })}
          </FootballField>
        </Stack>
      </BybVrDialogContent>

      <BybVrDialogFooter>
        <Stack direction="row" sx={{ width: "100%", gap: 2 }}>
          <Button onClick={selectRandomPositions} sx={{ flex: 1, padding: theme.spacing(2) }} color="secondary">
            {i18n("position.selection.dialog.actions.random")}
          </Button>
          <Button onClick={handleConfirm} sx={{ flex: 1, padding: theme.spacing(2) }}>
            {i18n("save")}
          </Button>
        </Stack>
      </BybVrDialogFooter>
    </BybVrDialog>
  );
};

export default PositionSelectorDialog;
